import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EntityAutocomplete } from "@/components/EntityAutocomplete";
import { UNIDAD_TIPO_LABELS } from "@/pages/Unidades";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Upload, AlertTriangle, CheckCircle2 } from "lucide-react";

const money = (n, currency = "USD") => (n == null ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 })} ${currency}`);

const emptyForm = () => ({
  origen_ciudad: "", destino_ciudad: "", client_id: null, client_label: "",
  tarifa_referencia: "", costo_techo: "", margen_objetivo_pct: "15", moneda: "USD", tipo_unidad: "",
  vigencia_desde: new Date().toISOString().slice(0, 10), vigencia_hasta: "",
});

export default function RateCards() {
  const { user } = useAuth();
  const isDueno = user?.rol === "dueno";
  const [data, setData] = useState({ items: [], total: 0 });
  const [origen, setOrigen] = useState("");
  const [destino, setDestino] = useState("");
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rate-cards", { params: { origen, destino } });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [origen, destino]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      origen_ciudad: r.origen_ciudad, destino_ciudad: r.destino_ciudad,
      client_id: r.client_id, client_label: r.client_id ? r.client_nombre : "",
      tarifa_referencia: r.tarifa_referencia, costo_techo: r.costo_techo,
      margen_objetivo_pct: r.margen_objetivo_pct, moneda: r.moneda, tipo_unidad: r.tipo_unidad || "",
      vigencia_desde: r.vigencia_desde, vigencia_hasta: r.vigencia_hasta || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        origen_ciudad: form.origen_ciudad, destino_ciudad: form.destino_ciudad,
        client_id: form.client_id || null,
        tarifa_referencia: parseFloat(form.tarifa_referencia || 0),
        costo_techo: parseFloat(form.costo_techo || 0),
        margen_objetivo_pct: parseFloat(form.margen_objetivo_pct || 0),
        moneda: form.moneda, tipo_unidad: form.tipo_unidad || null, vigencia_desde: form.vigencia_desde,
        vigencia_hasta: form.vigencia_hasta || null,
      };
      if (editingId) await api.put(`/rate-cards/${editingId}`, payload);
      else await api.post("/rate-cards", payload);
      toast.success(editingId ? "Tarifa actualizada" : "Tarifa creada");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/rate-cards/import-preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportPreview(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const confirmImport = async () => {
    setImporting(true);
    try {
      const rows = importPreview.rows.filter((r) => r._errores.length === 0).map((r) => ({
        origen_ciudad: r.origen_ciudad, destino_ciudad: r.destino_ciudad, client_codigo: r.client_codigo || null,
        tarifa_referencia: r.tarifa_referencia, costo_techo: r.costo_techo,
        margen_objetivo_pct: r.margen_objetivo_pct, moneda: r.moneda, tipo_unidad: r.tipo_unidad || null,
        vigencia_desde: r.vigencia_desde, vigencia_hasta: r.vigencia_hasta || null,
      }));
      const { data } = await api.post("/rate-cards/import-confirm", { rows });
      toast.success(`${data.insertados} tarifa(s) importada(s)`);
      setImportOpen(false);
      setImportPreview(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Tarifario"
        subtitle="Tarifas de referencia y costo techo por ruta — el resto de los roles ve en modo lectura"
        actions={isDueno && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)} className="gap-2" data-testid="rate-card-import-btn">
              <Upload className="h-4 w-4" /> Importar
            </Button>
            <Button onClick={openCreate} className="gap-2" data-testid="rate-card-new-btn">
              <Plus className="h-4 w-4" /> Nueva tarifa
            </Button>
          </div>
        )}
      />

      <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:w-1/2">
        <Input placeholder="Buscar origen..." value={origen} onChange={(e) => setOrigen(e.target.value)} data-testid="rate-card-search-origen" />
        <Input placeholder="Buscar destino..." value={destino} onChange={(e) => setDestino(e.target.value)} data-testid="rate-card-search-destino" />
      </div>

      <div className="overflow-x-auto rounded-md border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Origen</TableHead>
              <TableHead>Destino</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Tipo unidad</TableHead>
              <TableHead className="text-right">Tarifa ref.</TableHead>
              <TableHead className="text-right">Costo techo</TableHead>
              <TableHead className="text-right">Margen objetivo</TableHead>
              <TableHead>Vigencia</TableHead>
              <TableHead>Estado</TableHead>
              {isDueno && <TableHead />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((r) => (
              <TableRow key={r.id} data-testid={`rate-card-row-${r.id}`}>
                <TableCell className="text-sm">{r.origen_ciudad}</TableCell>
                <TableCell className="text-sm">{r.destino_ciudad}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.client_nombre}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.tipo_unidad ? (UNIDAD_TIPO_LABELS[r.tipo_unidad] || r.tipo_unidad) : "—"}</TableCell>
                <TableCell className="text-right font-mono text-xs">{money(r.tarifa_referencia, r.moneda)}</TableCell>
                <TableCell className="text-right font-mono text-xs">{money(r.costo_techo, r.moneda)}</TableCell>
                <TableCell className="text-right font-mono text-xs">{r.margen_objetivo_pct}%</TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.vigencia_desde} → {r.vigencia_hasta || "indefinido"}</TableCell>
                <TableCell>
                  <Badge className={r.activo ? "border border-green-200 bg-green-50 text-green-700 hover:bg-green-50" : "border border-slate-300 bg-slate-100 text-slate-600 hover:bg-slate-100"}>
                    {r.activo ? "Activa" : "Inactiva"}
                  </Badge>
                </TableCell>
                {isDueno && (
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(r)} data-testid={`rate-card-row-${r.id}-edit`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!loading && data.items.length === 0 && (
              <TableRow><TableCell colSpan={10} className="py-10 text-center text-muted-foreground">Sin tarifas registradas.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{editingId ? "Editar tarifa" : "Nueva tarifa"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Origen ciudad *</Label>
                <Input className="mt-1" value={form.origen_ciudad} onChange={(e) => setForm({ ...form, origen_ciudad: e.target.value })} data-testid="rate-card-form-origen" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Destino ciudad *</Label>
                <Input className="mt-1" value={form.destino_ciudad} onChange={(e) => setForm({ ...form, destino_ciudad: e.target.value })} data-testid="rate-card-form-destino" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Cliente (opcional — vacío = Todos)</Label>
              <div className="mt-1">
                <EntityAutocomplete type="clients" value={form.client_id} label={form.client_label}
                  onSelect={(c) => setForm({ ...form, client_id: c.id, client_label: `${c.codigo} — ${c.razon_social}` })}
                  testId="rate-card-form-client" />
              </div>
              {form.client_id && (
                <button type="button" className="mt-1 text-xs text-muted-foreground underline" onClick={() => setForm({ ...form, client_id: null, client_label: "" })}>
                  Quitar cliente (aplicar a Todos)
                </button>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Tarifa referencia *</Label>
                <Input type="number" className="mt-1" value={form.tarifa_referencia} onChange={(e) => setForm({ ...form, tarifa_referencia: e.target.value })} data-testid="rate-card-form-tarifa" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Costo techo *</Label>
                <Input type="number" className="mt-1" value={form.costo_techo} onChange={(e) => setForm({ ...form, costo_techo: e.target.value })} data-testid="rate-card-form-techo" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Margen objetivo %</Label>
                <Input type="number" className="mt-1" value={form.margen_objetivo_pct} onChange={(e) => setForm({ ...form, margen_objetivo_pct: e.target.value })} data-testid="rate-card-form-margen" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Moneda</Label>
                <Select value={form.moneda} onValueChange={(v) => setForm({ ...form, moneda: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="USD">USD</SelectItem><SelectItem value="MXN">MXN</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Tipo de unidad (opcional — nacional MX)</Label>
                <Select value={form.tipo_unidad || "cualquiera"} onValueChange={(v) => setForm({ ...form, tipo_unidad: v === "cualquiera" ? "" : v })}>
                  <SelectTrigger className="mt-1" data-testid="rate-card-form-tipo-unidad"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cualquiera">Cualquiera</SelectItem>
                    {Object.entries(UNIDAD_TIPO_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Vigencia desde *</Label>
                <Input type="date" className="mt-1" value={form.vigencia_desde} onChange={(e) => setForm({ ...form, vigencia_desde: e.target.value })} data-testid="rate-card-form-desde" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Vigencia hasta</Label>
              <Input type="date" className="mt-1 max-w-xs" value={form.vigencia_hasta} onChange={(e) => setForm({ ...form, vigencia_hasta: e.target.value })} data-testid="rate-card-form-hasta" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.origen_ciudad || !form.destino_ciudad || !form.tarifa_referencia || !form.costo_techo} data-testid="rate-card-form-save">
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) setImportPreview(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-heading">Importar tarifario (Excel / CSV)</DialogTitle></DialogHeader>
          {!importPreview ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Columnas esperadas: origen_ciudad, destino_ciudad, client_codigo (opcional), tarifa_referencia,
                costo_techo, margen_objetivo_pct, moneda, tipo_unidad (opcional — nacional MX), vigencia_desde, vigencia_hasta.
              </p>
              <Input type="file" accept=".xlsx,.xls,.csv" ref={fileRef} onChange={onFileChosen} data-testid="rate-card-import-file" />
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm">
                {importPreview.total} fila(s) leídas — <span className="text-red-600">{importPreview.con_errores} con errores</span>.
                Solo se importarán las filas sin errores.
              </p>
              <div className="max-h-80 overflow-auto rounded-md border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fila</TableHead><TableHead>Origen</TableHead><TableHead>Destino</TableHead>
                      <TableHead>Cliente</TableHead><TableHead>Techo</TableHead><TableHead>Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importPreview.rows.map((r, i) => (
                      <TableRow key={i} data-testid={`rate-card-import-row-${i}`}>
                        <TableCell className="text-xs">{r._fila}</TableCell>
                        <TableCell className="text-xs">{r.origen_ciudad}</TableCell>
                        <TableCell className="text-xs">{r.destino_ciudad}</TableCell>
                        <TableCell className="text-xs">{r.client_nombre}</TableCell>
                        <TableCell className="text-xs">{r.costo_techo} {r.moneda}</TableCell>
                        <TableCell>
                          {r._errores.length === 0 ? (
                            <span className="inline-flex items-center gap-1 text-xs text-green-700"><CheckCircle2 className="h-3.5 w-3.5" /> OK</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-red-600" title={r._errores.join(", ")}>
                              <AlertTriangle className="h-3.5 w-3.5" /> {r._errores.join(", ")}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setImportOpen(false); setImportPreview(null); }}>Cancelar</Button>
            {importPreview && (
              <Button onClick={confirmImport} disabled={importing || importPreview.total - importPreview.con_errores === 0} data-testid="rate-card-import-confirm">
                {importing ? "Importando..." : `Confirmar importación (${importPreview.total - importPreview.con_errores})`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
